import type * as React from "react";
import { useEffect } from "react";

import { Box, Grid2 as Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useEventCards } from "routes/dashboard/default/queries/useEventCards";
import { cards } from "routes/dashboard/default/utils/cards";
import { SkeletonLayout } from "shared/atoms/SkeletonLayout";

import { EventCard } from "./EventCard/EventCard";

type Props = {
  unreadMessagesCount: number;
};

const EventCardSkeleton: React.FC = () => (
  <SkeletonLayout
    layout={{
      type: "row",
      gap: 2,
      alignItems: "center",
      sx: {
        padding: { xs: "12px 10px", md: "24px 20px" },
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "12px",
        minHeight: 42,
        bgcolor: "background.paper",
      },
      children: [
        { type: "skeleton", variant: "circular", width: 18, height: 18 },
        { type: "skeleton", variant: "text", width: "40%", height: 20 },
        { type: "skeleton", variant: "rounded", width: 32, height: 32, sx: { marginLeft: "auto" } },
      ],
    }}
  />
);

export const EventCardsGrid: React.FC<Props> = ({ unreadMessagesCount }) => {
  const { t } = useTranslation();
  const { data, error, isLoading, refetch } = useEventCards();

  const filteredCards = cards.filter(card => {
    const cardData = data?.[card.label];
    return cardData !== null;
  });

  useEffect(() => {
    if (unreadMessagesCount > 0) {
      refetch();
    }
  }, [unreadMessagesCount, refetch]);

  return (
    <Box component="section">
      <Grid container spacing={{ xs: 1, md: 2 }} columns={{ xs: 1, sm: 1, md: 2, lg: 3 }}>
        {!isLoading
          ? filteredCards.map(({ label, link, icon, dataTestId }) => (
              <Grid key={dataTestId} size={{ xs: 1 }}>
                <EventCard
                  linkTo={link}
                  label={t(`dashboard.event_cards.${label}`) as string}
                  icon={icon}
                  notifications={data?.[label] ?? 0}
                  error={error}
                  dataTestId={dataTestId}
                />
              </Grid>
            ))
          : [...Array(6)].map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Grid key={index} size={{ xs: 1 }}>
                <EventCardSkeleton />
              </Grid>
            ))}
      </Grid>
    </Box>
  );
};
